import { LogEntry, LogEntryType } from '@/parsing/LogEntry';

import { TimelineMarker } from '../../TimelineData';

export type IosRouterEventType = 'attach' | 'detach';

export interface IosRouterEvent {
  type: IosRouterEventType;
  childName: string;
  childIdentifier: string;
  parentName: string;
}

export interface IosRouterTreeNodeSnapshot {
  key: string;
  label: string;
  state?: 'detaching';
  children: IosRouterTreeNodeSnapshot[];
}

export interface IosRouterTreeSnapshot {
  event: IosRouterEvent;
  rootNodes: IosRouterTreeNodeSnapshot[];
  highlightedNodeKey: string | undefined;
}

export interface IosRouterTimelineMarker extends TimelineMarker {
  iosRouterTreeSnapshot: IosRouterTreeSnapshot;
}

type MutableRouterNode = {
  key: string;
  label: string;
  name: string;
  children: MutableRouterNode[];
  parent: MutableRouterNode | undefined;
  sequence: number;
  detached: boolean;
};

export function parseIosRouterEvent(content: string): IosRouterEvent | undefined {
  const attachMatch = content.match(/^Attaching (.+)\((.*)\) to (.+)$/);
  if (attachMatch) {
    return {
      type: 'attach',
      childName: attachMatch[1],
      childIdentifier: attachMatch[2],
      parentName: attachMatch[3],
    };
  }

  const detachMatch = content.match(/^Detaching (.+)\((.*)\) from (.+)$/);
  if (detachMatch) {
    return {
      type: 'detach',
      childName: detachMatch[1],
      childIdentifier: detachMatch[2],
      parentName: detachMatch[3],
    };
  }

  return undefined;
}

export function isIosRouterTimelineMarker(
  marker: TimelineMarker,
): marker is IosRouterTimelineMarker {
  return typeof (marker as Partial<IosRouterTimelineMarker>).iosRouterTreeSnapshot !== 'undefined';
}

export function buildIosRouterTreeSnapshotsByLogEntry(
  entries: LogEntry[],
): Map<number, IosRouterTreeSnapshot> {
  const snapshotsByLogEntry = new Map<number, IosRouterTreeSnapshot>();
  const builder = new IosRouterTreeBuilder();

  entries.forEach((entry) => {
    if (entry.entryType !== LogEntryType.Generic) {
      return;
    }
    if (entry.extra.generic?.module !== 'RIDERCORE.ROUTER') {
      return;
    }

    const snapshot = builder.apply(entry.content);
    if (snapshot) {
      snapshotsByLogEntry.set(entry.index, snapshot);
    }
  });

  return snapshotsByLogEntry;
}

export class IosRouterTreeBuilder {
  private nodesByKey = new Map<string, MutableRouterNode>();
  private rootNodes: MutableRouterNode[] = [];
  private nextSequence = 0;

  apply(content: string): IosRouterTreeSnapshot | undefined {
    const event = parseIosRouterEvent(content);
    if (!event) {
      return undefined;
    }

    if (event.type === 'attach') {
      const child = this.attach(event);
      return this.snapshot(event, child.key);
    }

    const child = this.findChild(event);
    if (child && child.children.length > 0) {
      child.detached = true;
    }

    const snapshot = this.snapshot(event, child?.key);
    if (child) {
      this.detach(child);
    }
    return snapshot;
  }

  private attach(event: IosRouterEvent): MutableRouterNode {
    const parent =
      this.findParent(event.parentName) || this.createSyntheticParent(event.parentName);
    const child = this.getOrCreateChild(event);

    if (parent === child) {
      return child;
    }

    if (this.isAncestor(child, parent)) {
      this.detachFromParent(parent);
    }

    this.detachFromParent(child);
    this.removeRoot(child);

    child.detached = false;
    child.parent = parent;
    parent.children.push(child);

    return child;
  }

  private detach(child: MutableRouterNode) {
    if (child.children.length > 0) {
      child.detached = true;
      return;
    }

    this.detachFromParent(child);

    child.detached = true;
    this.removeDetachedLeaf(child);
  }

  private findParent(parentName: string): MutableRouterNode | undefined {
    const candidates = Array.from(this.nodesByKey.values()).filter(
      (node) => node.name === parentName,
    );
    return candidates.sort((a, b) => b.sequence - a.sequence)[0];
  }

  private findChild(event: IosRouterEvent): MutableRouterNode | undefined {
    const child = this.nodesByKey.get(this.childKey(event.childName, event.childIdentifier));
    if (!child) {
      return undefined;
    }

    if (child.parent?.name === event.parentName) {
      return child;
    }

    return undefined;
  }

  private getOrCreateChild(event: IosRouterEvent): MutableRouterNode {
    const key = this.childKey(event.childName, event.childIdentifier);
    const existing = this.nodesByKey.get(key);
    if (existing) {
      return existing;
    }

    return this.createNode({
      key,
      label: key,
      name: event.childName,
    });
  }

  private createSyntheticParent(parentName: string): MutableRouterNode {
    return this.createNode({
      key: parentName,
      label: parentName,
      name: parentName,
    });
  }

  private createNode(args: { key: string; label: string; name: string }): MutableRouterNode {
    const node: MutableRouterNode = {
      key: args.key,
      label: args.label,
      name: args.name,
      children: [],
      parent: undefined,
      sequence: this.nextSequence,
      detached: false,
    };
    this.nextSequence += 1;
    this.nodesByKey.set(node.key, node);
    this.addRoot(node);
    return node;
  }

  private detachFromParent(node: MutableRouterNode) {
    if (!node.parent) {
      return;
    }

    const parent = node.parent;
    node.parent = undefined;
    parent.children = parent.children.filter((child) => child !== node);
    this.addRoot(node);
    this.removeDetachedLeaf(parent);
  }

  private isAncestor(ancestor: MutableRouterNode, node: MutableRouterNode): boolean {
    let current: MutableRouterNode | undefined = node.parent;
    const visited = new Set<MutableRouterNode>();

    while (current) {
      if (current === ancestor) {
        return true;
      }
      if (visited.has(current)) {
        return false;
      }
      visited.add(current);
      current = current.parent;
    }

    return false;
  }

  private addRoot(node: MutableRouterNode) {
    if (this.rootNodes.includes(node)) {
      return;
    }
    this.rootNodes.push(node);
  }

  private removeRoot(node: MutableRouterNode) {
    this.rootNodes = this.rootNodes.filter((rootNode) => rootNode !== node);
  }

  private removeDetachedLeaf(node: MutableRouterNode) {
    if (!node.detached || node.children.length > 0) {
      return;
    }

    this.detachFromParent(node);
    this.removeRoot(node);
    this.nodesByKey.delete(node.key);
  }

  private snapshot(
    event: IosRouterEvent,
    highlightedNodeKey: string | undefined,
  ): IosRouterTreeSnapshot {
    return {
      event,
      rootNodes: this.rootNodes.map((node) => this.snapshotNode(node)),
      highlightedNodeKey,
    };
  }

  private snapshotNode(node: MutableRouterNode): IosRouterTreeNodeSnapshot {
    return {
      key: node.key,
      label: node.label,
      state: node.detached ? 'detaching' : undefined,
      children: node.children.map((child) => this.snapshotNode(child)),
    };
  }

  private childKey(name: string, identifier: string): string {
    return `${name}(${identifier})`;
  }
}
